import { Fragment, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Menu, Transition } from '@headlessui/react'
import { BellAlertIcon, BellIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { useServers, useBatchServerResources } from '@/lib/queries/serverQueries'
import { getServerStatus, panelResourceIdentifier, canObserveServerResources } from '@/lib/server-utils'

/**
 * Bell indicator shown in the top bar. Power state alone does not establish
 * charges: a stopped server may still be in a free trial or retained recovery.
 */
export default function OfflineServersAlert() {
  const { data: servers = [] } = useServers()
  const serverIds = useMemo(
    () => servers.filter(canObserveServerResources).map(panelResourceIdentifier),
    [servers]
  )
  const { data: resourcesData } = useBatchServerResources(serverIds)

  const offlineServers = useMemo(() => {
    // Wait until live resource data has loaded before flagging anything as
    // offline. getServerStatus() defaults to 'offline' when no state is known,
    // which would otherwise make the bell flash amber on first load.
    if (!resourcesData) return []
    return (servers as any[])
      .map((server: any) => {
        const key = panelResourceIdentifier(server)
        const live = resourcesData?.[key]
        return { ...server, _state: live?.current_state, _hasLiveState: !!live?.current_state }
      })
      // Only count a server once its live state is confirmed offline.
      .filter((server: any) => server._hasLiveState && getServerStatus(server) === 'offline')
  }, [servers, resourcesData])

  const count = offlineServers.length

  return (
    <Menu as="div" className="relative inline-block text-left">
      <Menu.Button
        className="relative inline-flex items-center justify-center rounded-md p-2 text-gray-500 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#2a2a2e] focus:outline-none focus:ring-2 focus:ring-primary-500"
        title={count > 0 ? `${count} powered-off server${count === 1 ? '' : 's'} — review billing` : 'No billing alerts'}
        aria-label="Billing alerts"
      >
        {count > 0 ? (
          <BellAlertIcon className="h-5 w-5 text-amber-500" />
        ) : (
          <BellIcon className="h-5 w-5" />
        )}
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-semibold leading-none text-white">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 z-[100] mt-2 w-80 origin-top-right rounded-md bg-white dark:bg-[#252526] shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          <div className="px-4 py-3 border-b border-gray-100 dark:border-[#2d2d2d]">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-white">
              <ExclamationTriangleIcon className="h-4 w-4 text-amber-500" />
              Alerts
            </div>
          </div>
          {count === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
              No alerts, nice!
            </div>
          ) : (
            <>
              <div className="px-4 py-3 text-xs text-amber-600 dark:text-amber-300/90 leading-relaxed">
                These servers are powered off. Check each server’s billing for
                its current spend and any minimum that applies.
              </div>
              <div className="max-h-64 overflow-y-auto py-1">
                {offlineServers.map((server: any) => {
                  const id = server.identifier || server.uuid
                  return (
                    <Menu.Item key={id}>
                      {({ active }) => (
                        <Link
                          to={`/servers/${id}/overview`}
                          className={`${active ? 'bg-gray-50 dark:bg-[#2a2a2e]' : ''} flex items-center justify-between gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200`}
                        >
                          <span className="truncate font-medium">{server.name}</span>
                          <span className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-full bg-gray-100 dark:bg-slate-600/40 px-2 py-0.5 text-[11px] font-medium text-gray-600 dark:text-gray-300">
                            <span className="h-1.5 w-1.5 rounded-full bg-gray-400 dark:bg-gray-500" />
                            Offline
                          </span>
                        </Link>
                      )}
                    </Menu.Item>
                  )
                })}
              </div>
              <div className="border-t border-gray-100 dark:border-[#2d2d2d] px-4 py-2">
                <Link
                  to="/billing-overview"
                  className="block text-center text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline"
                >
                  View billing overview
                </Link>
              </div>
            </>
          )}
        </Menu.Items>
      </Transition>
    </Menu>
  )
}
